import {
  date,
  jsonb,
  pgEnum,
  pgTable,
  smallint,
  text,
} from 'drizzle-orm/pg-core';
import {
  defaultFields,
  defaultHistoryFields,
  defaultIndexes,
} from '@trashlab/api/drizzle/default';
import { sql } from 'drizzle-orm';
import { prisma } from '@trashlab/api/src/library/server/prisma';
import { Prisma } from '@trashlab/api/src/db/generated';
import type { EligibleDateRange } from '@trashlab/api/src/domains/orders/shared/month-eligibility';

export const recurrence_settings_version = pgEnum(
  'recurrence_settings_version',
  ['V1', 'V2'],
);

export const recurrence_settings = pgTable(
  'recurrence_settings',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** Version marker for legacy vs new configurations */
    version: recurrence_settings_version('version').default('V1').notNull(),
    /** Optional human-friendly name for reusable selections */
    name: text('name'),
    /** Optional description for additional context */
    description: text('description'),
    /**
     * MONTHLY = 1
     * WEEKLY  = 2
     */
    frequency: smallint('frequency').notNull(),

    /**
     * Interval between services
     */
    interval: smallint('interval').notNull(),

    /**
     * MONDAY    = 0
     * TUESDAY   = 1
     * WEDNESDAY = 2
     * THURSDAY  = 3
     * FRIDAY    = 4
     * SATURDAY  = 5
     * SUNDAY    = 6
     */
    days: jsonb('days')
      .$type<number[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),

    /**
     * Position of selected days in the month
     *
     * FIRST  = 1
     * SECOND = 2
     * THIRD  = 3
     * FOURTH = 4
     * LAST   = -1
     */
    dayPositions: jsonb('dayPositions')
      .$type<number[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),

    /**
     * Start date for when the service is eligible.
     * null means the service is eligible year-round (no start restriction).
     *
     * Example: '04-01' for April 1st (MM-DD format stored as date for validation)
     */
    eligibleStartDate: date('eligibleStartDate'),

    /**
     * End date for when the service is eligible.
     * null means the service is eligible year-round (no end restriction).
     *
     * Example: '11-30' for November 30th (MM-DD format stored as date for validation)
     */
    eligibleEndDate: date('eligibleEndDate'),
    eligibleDateRanges: jsonb('eligibleDateRanges').$type<EligibleDateRange[]>(),
  },
  (table) => {
    return {
      ...defaultIndexes.indexesWithTenantId('recurrence_settings', table),
    };
  },
);

export const recurrence_settings_history = pgTable(
  'recurrence_settings_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    version: recurrence_settings_version('version'),
    name: text('name'),
    description: text('description'),
    frequency: smallint('frequency'),
    interval: smallint('interval'),
    days: jsonb('days'),
    dayPositions: jsonb('dayPositions'),
    eligibleStartDate: date('eligibleStartDate'),
    eligibleEndDate: date('eligibleEndDate'),
    eligibleDateRanges: jsonb('eligibleDateRanges').$type<EligibleDateRange[]>(),
  },
);

export type RecurrenceSettings = Prisma.Result<
  typeof prisma.recurrence_settings,
  {},
  'findFirstOrThrow'
>;
